// External libraries
import * as React from "react";
import { inject, observer } from "mobx-react";
import { withLocalization } from "bernie-l10n";
import { UitkFigure, UitkFigureAspectRatioType, UitkImage } from "uitk-react-images";
import { classNames } from "uitk-react-utils";
import { UitkLoader } from "uitk-react-loader";

// Components
import { MapsProps, MapsInjectedProps } from "../typings";
import { useFullscreenOpen } from "../hooks";
import { MapContext } from "../MapContext";

const ModuleMap = React.lazy(() => import(/* webpackChunkName: "module-map" */ "../ModuleMap"));

const isInViewport = (element: HTMLDivElement): boolean => {
  const distance = element.getBoundingClientRect();
  if (!distance) {
    return false;
  }

  return (
    distance.bottom > (window.innerHeight || document.documentElement.clientHeight) &&
    distance.top < (window.innerHeight || document.documentElement.clientHeight)
  );
};

const DynamicMaps: React.FC<MapsProps> = (props: MapsProps & MapsInjectedProps) => {
  const { sendUserToSRP, toggleShowFullscreenMap } = useFullscreenOpen(props);
  const { staticMap, flexComponent, context } = props;
  const shouldOpenMapInSRP = false;
  const countryCode = (context && context.site && context.site.country) || "";
  const elemRef = React.useRef<HTMLDivElement | null>(null);
  const placeHolderAltText =
    (context &&
      context.searchContext &&
      context.searchContext.location &&
      context.searchContext.location.extendedName) ||
    "";

  if (typeof window === "undefined") {
    staticMap.generateGooglePlaceHolderMapImage(context, countryCode);
  }

  const [isLoaderVisible, showLoader] = React.useState(true);

  const [isBottomSheetSticky, setBottomSheetPosition] = React.useState(true);

  const [isClient, setIsClient] = React.useState(false);

  React.useEffect(() => {
    setIsClient(true);
  }, []);

  // Handler anchoring bottom sheet to the bottom of the window
  React.useEffect(() => {
    const viewportSizeCheck = () => {
      setBottomSheetPosition(isInViewport(elemRef.current as HTMLDivElement));
    };
    window.addEventListener("scroll", viewportSizeCheck);

    return () => window.removeEventListener("scroll", viewportSizeCheck);
  }, []);

  const toggleLoader = () => {
    showLoader(!isLoaderVisible);
  };

  return (
    <div
      className="Maps uitk-flex uitk-cell all-cell-1-1 all-y-padding-three"
      id={flexComponent.id}
      data-fm={flexComponent.fmId}
      data-fm-title-id={flexComponent.fmTitleId}
    >
      <UitkFigure ratio={UitkFigureAspectRatioType.R21_9}>
        {staticMap && staticMap.signedPlaceHolderUrl && (
          <UitkImage alt={placeHolderAltText} src={staticMap.signedPlaceHolderUrl} data-testid="placeholder-map" />
        )}
        {isLoaderVisible && <UitkLoader className="dynamicMapLoader" />}
        <div ref={elemRef} className={classNames("uitk-flex dynamicMap")} data-testid="dynamic-map">
          <MapContext.Provider
            value={{
              shouldOpenMapInSRP,
              sendUserToSRP,
              toggleShowFullscreenMap,
            }}
          >
            {isClient && (
              <React.Suspense fallback={<UitkLoader className="dynamicMapLoader" />}>
                <ModuleMap
                  {...props}
                  classNames={classNames(
                    "uitk-dialog-content uitk-dynamic-map uitk-cell uitk-grid",
                    isBottomSheetSticky && "setStickyBottomSheet"
                  )}
                  onMapMounted={toggleLoader}
                  isDynamic={true}
                />
              </React.Suspense>
            )}
          </MapContext.Provider>
        </div>
      </UitkFigure>
    </div>
  );
};

export const Maps = withLocalization<MapsProps, React.FC<MapsProps>>(
  inject("analytics", "hotels", "staticMap", "context", "poi")(observer(DynamicMaps))
);

Maps.displayName = "DynamicMaps";

export default Maps;
